import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api/client";

const appName = process.env.REACT_APP_APP_NAME || "Jasmine";

function hasPerm(user, perm) {
  return (user?.permissions || []).includes(perm);
}

function hasRole(user, roles) {
  return roles.some((r) => (user?.roles || []).includes(r));
}

const NAV_MAIN = [
  { to: "/chat", icon: "chat", label: "Chat", always: true },
  { to: "/knowledge", icon: "library_books", label: "Knowledge", always: true },
  { to: "/voice", icon: "mic", label: "Voice", perm: "chat.use_voice" },
  { to: "/audit", icon: "assignment", label: "Audit Logs", perm: "audit.read" },
  { to: "/profile", icon: "account_circle", label: "Profil Saya", always: true },
];

const NAV_ADMIN = [
  { to: "/users", icon: "group", label: "Users", perm: "user.manage" },
  { to: "/roles", icon: "admin_panel_settings", label: "Roles & Perms", perm: "role.manage" },
  { to: "/debug/qwen-vl", icon: "visibility", label: "Qwen-VL Debug", perm: "audit.read" },
];

function NavLink({ to, icon, label, active, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
        active
          ? "bg-white/10 text-white"
          : "text-[#b4b4b4] hover:bg-white/5 hover:text-white"
      }`}
    >
      <span
        className="material-symbols-outlined text-[20px]"
        style={{ fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0" }}
      >
        {icon}
      </span>
      <span>{label}</span>
    </Link>
  );
}

export default function Sidebar({ user, sessions, activeSessionId, openSession, createSession, requestArchiveSession, requestDeleteSession, logout }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuSessionId, setMenuSessionId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [chatSearchResults, setChatSearchResults] = useState([]);
  const [chatSearchLoading, setChatSearchLoading] = useState(false);
  const [chatSearchError, setChatSearchError] = useState("");

  const visibleAdmin = NAV_ADMIN.filter((item) => hasPerm(user, item.perm));
  const isAdmin = visibleAdmin.length > 0;
  const activeMenuSession = sessions.find((session) => session.id === menuSessionId);
  const normalizedSearchQuery = chatSearchQuery.trim();
  const searchActive = normalizedSearchQuery.length >= 2;

  const clearChatSearch = () => {
    setChatSearchQuery("");
    setChatSearchResults([]);
    setChatSearchError("");
    setChatSearchLoading(false);
  };

  const closeSessionMenu = () => {
    setMenuSessionId(null);
    setMenuPosition(null);
  };

  const toggleSessionMenu = (event, sessionId) => {
    event.stopPropagation();
    if (menuSessionId === sessionId) {
      closeSessionMenu();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const width = 216;
    const height = 94;
    const gap = 8;
    const viewportPadding = 12;
    const left = Math.min(
      window.innerWidth - width - viewportPadding,
      Math.max(viewportPadding, rect.right - width)
    );
    const opensUp = rect.bottom + height + gap > window.innerHeight - viewportPadding;
    const top = opensUp
      ? Math.max(viewportPadding, rect.top - height - gap)
      : Math.min(window.innerHeight - height - viewportPadding, rect.bottom + gap);

    setMenuPosition({ top, left, width });
    setMenuSessionId(sessionId);
  };

  useEffect(() => {
    if (!menuSessionId) return undefined;
    const close = () => closeSessionMenu();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", close, true);
    };
  }, [menuSessionId]);

  useEffect(() => {
    if (normalizedSearchQuery.length < 2) {
      setChatSearchResults([]);
      setChatSearchError("");
      setChatSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setChatSearchLoading(true);
    setChatSearchError("");
    const timer = window.setTimeout(() => {
      api.searchChats(normalizedSearchQuery, 20)
        .then((rows) => {
          if (!cancelled) setChatSearchResults(rows);
        })
        .catch((err) => {
          if (!cancelled) {
            setChatSearchResults([]);
            setChatSearchError(err.message || "Gagal mencari chat");
          }
        })
        .finally(() => {
          if (!cancelled) setChatSearchLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [normalizedSearchQuery]);

  const openSearchResult = (sessionId) => {
    openSession(sessionId);
    setMobileOpen(false);
    clearChatSearch();
  };

  const resultRoleLabel = (result) => {
    if (result.match_type === "title") return "Judul";
    if (result.role === "user") return "Anda";
    if (result.role === "assistant") return appName;
    return "Pesan";
  };

  const sidebarContent = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
        </div>
        <div className="min-w-0">
          <p className="text-white font-bold text-sm leading-tight truncate">{appName}</p>
          <p className="text-[#8d929b] text-xs">AI internal PT Sinar Jernih Suksesindo</p>
        </div>
      </div>

      {/* New Chat */}
      <div className="px-3 py-2 flex-shrink-0">
        <button
          onClick={() => { createSession(); setMobileOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#ececf1] text-sm font-medium transition-colors border border-white/10"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          New Chat
        </button>
      </div>

      {/* Chat Search */}
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#8d929b] text-[18px]">search</span>
          <input
            value={chatSearchQuery}
            onChange={(event) => setChatSearchQuery(event.target.value)}
            className="w-full h-10 rounded-lg bg-black/15 border border-white/10 pl-9 pr-9 text-sm text-[#ececf1] placeholder:text-[#8d929b] outline-none focus:border-[#10a37f]/70 focus:bg-black/20 transition-colors"
            placeholder="Search chat"
            type="search"
            autoComplete="off"
          />
          {chatSearchQuery && (
            <button
              type="button"
              onClick={clearChatSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-[#8d929b] hover:bg-white/10 hover:text-white transition-colors"
              title="Clear search"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Nav */}
      <nav className="px-3 space-y-0.5 flex-shrink-0">
        {NAV_MAIN.filter((item) => item.always || hasPerm(user, item.perm) || (item.perm === "audit.read" && hasRole(user, ["SUPER_ADMIN", "AUDITOR", "MANAGEMENT"]))).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={item.label}
            active={currentPath === item.to || (item.to === "/chat" && currentPath === "/")}
            onClick={() => setMobileOpen(false)}
          />
        ))}
      </nav>

      {/* Admin Nav */}
      {isAdmin && (
        <div className="flex-shrink-0">
          <div className="mx-4 my-2 border-t border-white/10" />
          <div className="px-4 mb-1">
            <span className="text-[#8d929b] text-[10px] font-semibold uppercase tracking-widest">Admin</span>
          </div>
          <nav className="px-3 space-y-0.5">
            {visibleAdmin.map((item) => (
              <NavLink key={item.to} to={item.to} icon={item.icon} label={item.label} active={currentPath === item.to} onClick={() => setMobileOpen(false)} />
            ))}
          </nav>
        </div>
      )}

      {/* Divider + Recent Chats */}
      <div className="mx-4 my-2 border-t border-white/10 flex-shrink-0" />
      <div className="px-4 mb-1.5 flex items-center justify-between flex-shrink-0">
        <span className="text-[#8d929b] text-[10px] font-semibold uppercase tracking-widest">
          {searchActive ? "Search Results" : "Recent Chats"}
        </span>
        {searchActive && chatSearchLoading && (
          <span className="material-symbols-outlined text-[#8d929b] text-[14px] animate-spin">progress_activity</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 space-y-0.5 min-h-0 pb-2">
        {searchActive ? (
          <>
            {chatSearchResults.map((result) => (
              <button
                key={`${result.session_id}-${result.message_id || "title"}`}
                type="button"
                className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                  result.session_id === activeSessionId ? "bg-white/10" : "hover:bg-white/5"
                }`}
                onClick={() => openSearchResult(result.session_id)}
              >
                <span className="block text-sm font-medium text-[#ececf1] truncate">
                  {result.title || "Chat baru"}
                </span>
                <span className="mt-1 flex items-start gap-1.5 text-xs leading-snug text-[#8d929b]">
                  <span className="mt-[1px] shrink-0 text-[10px] font-semibold uppercase text-[#10a37f]">
                    {resultRoleLabel(result)}
                  </span>
                  <span
                    className="min-w-0 overflow-hidden"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {result.snippet || "Match di chat ini"}
                  </span>
                </span>
              </button>
            ))}
            {!chatSearchLoading && chatSearchError && (
              <p className="px-3 py-2 text-xs text-red-300">{chatSearchError}</p>
            )}
            {!chatSearchLoading && !chatSearchError && !chatSearchResults.length && (
              <p className="px-3 py-2 text-xs text-[#8d929b]">Tidak ada chat yang cocok.</p>
            )}
          </>
        ) : (
          <>
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`group rounded-lg transition-colors ${
                  session.id === activeSessionId ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                <div className="flex items-center justify-between">
                  <button
                    className="flex-1 min-w-0 text-left px-3 py-2 text-sm truncate text-[#b4b4b4] hover:text-white transition-colors"
                    onClick={() => { openSession(session.id); setMobileOpen(false); }}
                  >
                    {session.title || "Chat baru"}
                  </button>
                  <button
                    className="opacity-100 md:opacity-0 group-hover:opacity-100 px-2 py-2 text-[#8d929b] hover:text-white transition-all flex-shrink-0"
                    onClick={(event) => toggleSessionMenu(event, session.id)}
                    title="Opsi chat"
                  >
                    <span className="material-symbols-outlined text-[18px]">more_horiz</span>
                  </button>
                </div>
              </div>
            ))}
            {!sessions.length && (
              <p className="px-3 py-2 text-xs text-[#8d929b]">Belum ada session.</p>
            )}
          </>
        )}
      </div>

      {/* User Footer */}
      <div className="border-t border-white/10 p-3 space-y-1 flex-shrink-0">
        <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors group no-underline">
          <div className="w-8 h-8 rounded-full bg-[#10a37f] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {(user?.full_name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#ececf1] truncate">{user?.full_name || "User"}</p>
            <p className="text-xs text-[#8d929b]">{(user?.roles || []).join(", ") || "User"}</p>
          </div>
          <span className="material-symbols-outlined text-[18px] text-[#8d929b] group-hover:text-white transition-colors">edit</span>
        </Link>
        <button onClick={logout} className="w-full flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[#8d929b] hover:bg-white/5 hover:text-white text-sm transition-colors">
          <span className="material-symbols-outlined text-[18px]">logout</span>
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile/tablet toggle button — hidden on lg+ (1024px) */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 w-9 h-9 bg-[#202123] rounded-lg flex items-center justify-center text-white shadow-lg"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <span className="material-symbols-outlined text-[20px]">{mobileOpen ? "close" : "menu"}</span>
      </button>

      {/* Mobile/tablet overlay backdrop */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar — fixed overlay on mobile/tablet (<1024px), static flex item on desktop (1024px+) */}
      <aside
        className={`fixed left-0 top-0 h-screen z-50 w-[260px] bg-[#202123] flex flex-col transition-transform duration-300
          lg:static lg:h-full lg:translate-x-0 lg:z-auto lg:flex-shrink-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {sidebarContent}
      </aside>

      {activeMenuSession && menuPosition && createPortal(
        <div
          className="rounded-xl bg-[#343541] border border-white/10 shadow-2xl py-1 overflow-hidden"
          style={{
            position: "fixed",
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            zIndex: 80,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              const session = activeMenuSession;
              closeSessionMenu();
              requestArchiveSession(session);
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#ececf1] hover:bg-white/10 transition-colors text-left"
          >
            <span className="material-symbols-outlined text-[18px]">archive</span>
            Archive
          </button>
          <button
            type="button"
            onClick={() => {
              const session = activeMenuSession;
              closeSessionMenu();
              requestDeleteSession(session);
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
            Delete
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
